import React, { useRef, useState, useEffect } from "react";
import {
  DmsPage,
  intl,
  HTTP_STATUS,
  DmsTable,
  DmsTableDeleteButton,
  DmsButtonGroup,
  DmsFlexContainer,
  fetchData,
  DmsButton,
  DmsTableEditButton,
  DmsModal,
  DmsTextField
} from "dmsbasereact";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { DmsAutocomplete } from "../../components/DmsAutoComplete/DmsAutoComplete";

export const InstitutionalGroupPage = () => {
  const table = useRef(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [selectedPersons, setSelectedPersons] = useState([]);

  const [personMap, setPersonMap] = useState({});

  useEffect(() => {
    const fetchPersons = async () => {
      try {
        const response = await fetchData('/person/all', 'GET', true);
        if (response.data && response.data.data) {
          const persons = Array.isArray(response.data.data) ? response.data.data : response.data.data.rows;
          const map = Object.fromEntries(persons.map(p => [p.pk, p.name]));
          setPersonMap(map);
        }
      } catch (error) {
        console.error("Failed to fetch persons", error);
      }
    };
    fetchPersons();
  }, []);

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setNewGroupName("");
    setNewGroupDescription("");
    setSelectedPersons([]);
  };

  const handleCreateSave = async () => {
    if (!newGroupName) {
      Swal.fire({ icon: "warning", title: intl.get("MissingInformation").d("Missing Information"), text: intl.get("PleaseEnterName").d("Please enter a name.") });
      return;
    }

    try {
      const payload = {
        name: newGroupName,
        description: newGroupDescription,
        persons: selectedPersons,
      }
      const response = await fetchData('/institutional_group/', 'POST', true, payload);
      if (response.status !== HTTP_STATUS.CREATED_201) {
        throw new Error(response?.data?.detail || "Failed to save institutional group.");
      }

      Swal.fire({ icon: "success", title: intl.get("Success").d("Success"), text: intl.get("InstitutionalGroupCreated").d("Institutional group created successfully.") });
      table.current.updateTable();
      handleCloseCreateModal();
    } catch (error) {
      Swal.fire({ icon: "error", title: intl.get("Error").d("Error"), text: error.message });
    }
  };

  const header = [
    { label: intl.get("Name").d("Name"), column_name: "name", filter: true },
    { label: intl.get("Description").d("Description"), column_name: "description", filter: true, control: 'textarea' },
    {
      label: intl.get("Persons").d("Persons"),
      column_name: "person_associations",
      sortable: false,
      function: (associations) => {
        if (!Array.isArray(associations)) {
          return '-';
        }
        return associations
          .map(assoc => personMap[assoc.person])
          .filter(Boolean)
          .join(', ');
      },
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "120px",
      sortable: false,
      element: (row) => (
        <DmsButtonGroup>
          <DmsTableEditButton
            row={row}
            table={table}
            url="/institutional_group/"
            title={intl.get("EditInstitutionalGroup").d("Edit Institutional Group")}
          />
          <DmsTableDeleteButton
            table={table}
            title={intl.get("DeleteInstitutionalGroup").d("Delete Institutional Group")}
            text={intl.get("InstitutionalGroupPage.DeleteButtonMessage", { name: row.name }).d(`The group {name} will be deleted. Are you sure?`)}
            url="/institutional_group/"
            row_pk={row.pk}
          />
        </DmsButtonGroup>
      ),
    },
  ];

  const toolbar = (
    <DmsFlexContainer>
      <DmsFlexContainer.Left>
        <DmsButton variant="primary" icon={<AddIcon />} onClick={handleOpenCreateModal} text={intl.get("CreateInstitutionalGroup").d("Create Institutional Group")} />
      </DmsFlexContainer.Left>
    </DmsFlexContainer>
  );

  return (
    <DmsPage name={intl.get("InstitutionalGroupManagement").d("Institutional Group Management")}>
      <DmsTable header={header} managed={true} needs_authentication={true} ref={table} url={'/institutional_group/table'} toolbar={toolbar} />

      <DmsModal isVisible={isCreateModalOpen} onClose={handleCloseCreateModal}>
        <DmsModal.Header title={intl.get("CreateInstitutionalGroup").d("Create Institutional Group")} onClose={handleCloseCreateModal} />
        <DmsModal.Body>
          <Box sx={{ mt: 2 }}>
            <DmsTextField
              name="name"
              label={intl.get("Name").d("Name")}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              fullWidth
              required
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <DmsTextField
              name="description"
              label={intl.get("Description").d("Description")}
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            {/* <DmsSelect
                    label={intl.get("Persons").d("Persons")}
                    value={selectedPersons}
                    onChange={(value) => setSelectedPersons(value)}
                    url="/person/all"
                    properties={{
                        value: 'pk',
                        label: 'name'
                    }}
                    multiple
                    fullWidth
                /> */}
            <DmsAutocomplete
              label={intl.get("Persons").d("Persons")}
              value={selectedPersons}
              onChange={(value) => setSelectedPersons(value)}
              url="/person/all"
              properties={{
                value: 'pk',
                label: 'dms_user.user_name'
              }}
              multiple
              fullWidth
            />
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton variant="secondary" text={intl.get("Cancel").d("Cancel")} onClick={handleCloseCreateModal} />
          <DmsButton variant="primary" onClick={handleCreateSave} text={intl.get("Save").d("Save")} />
        </DmsModal.Footer>
      </DmsModal>
    </DmsPage>
  );
};